import { useCallback, useEffect, useRef, useState } from 'react';

interface CameraPreviewResult {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Сырой поток камеры — нужен тем, кто строит поверх него свой (например, размытие фона). */
  stream: MediaStream | null;
  isLoading: boolean;
  cameraError: string | null;
  isCameraEnabled: boolean;
  toggleCamera: () => void;
  stopStream: () => void;
}

export function useCameraPreview(initialEnabled = true): CameraPreviewResult {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const initialEnabledRef = useRef(initialEnabled);
  const [isLoading, setIsLoading] = useState(true);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraEnabled, setIsCameraEnabled] = useState(initialEnabled);
  const [stream, setStream] = useState<MediaStream | null>(null);

  useEffect(() => {
    let active = true;

    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stream.getVideoTracks().forEach((t) => {
          t.enabled = initialEnabledRef.current;
        });
        streamRef.current = stream;
        setStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        setIsLoading(false);
      } catch {
        if (!active) return;
        setCameraError('Нет доступа к камере. Проверьте разрешения в настройках браузера.');
        setIsLoading(false);
      }
    };

    startCamera();

    return () => {
      active = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const toggleCamera = useCallback(() => {
    streamRef.current?.getVideoTracks().forEach((t) => {
      t.enabled = !t.enabled;
    });
    setIsCameraEnabled((prev) => !prev);
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  return { videoRef, stream, isLoading, cameraError, isCameraEnabled, toggleCamera, stopStream };
}
